import { useRef, useState, memo, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { ThemeProvider, theme, type Theme } from 'configs/theme-configurator';
import type { User } from 'models';

import Menu from 'shared/components/Menu';

import {
  KeyIcon,
  InvoiceIcon,
  ExitIcon,
  UserIcon,
  BorderUserIcon,
} from 'components';

import { Container, StyledLink, StyledMenuItem } from './styled';

interface UserSettingsPickerProps {
  user: User | null;
  privacyPolicy: string | null;
}

const UserSettingsPicker = ({
  user,
  privacyPolicy,
}: UserSettingsPickerProps) => {
  const menuIconSxProps = useRef<SxProps<Theme>>({
    marginRight: '10px',
    fontSize: '1.1rem',
    color: '#CBCBCB',
  }).current;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  // TODO: carry out theme
  return (
    <ThemeProvider theme={theme}>
      <Container>
        <IconButton
          sx={{ borderRadius: 0 }}
          ref={anchorRef}
          onClick={handleToggle}
        >
          <BorderUserIcon
            sx={{
              fontSize: '1.5rem',
              color: '#fff',
              ...(open && {
                color: (theme) => theme.palette.secondary.main,
              }),
            }}
          />
        </IconButton>
        <Menu anchorEl={anchorRef.current} open={open} onClose={handleClose}>
          <StyledMenuItem onClick={handleClose}>
            <StyledLink to="/person-information">
              <UserIcon
                sx={{
                  ...menuIconSxProps,
                  fontSize: '1.2rem',
                  marginRight: '8px',
                }}
              />
              {user?.FullName}
            </StyledLink>
          </StyledMenuItem>
          <StyledMenuItem>
            <StyledLink to="/password">
              <KeyIcon sx={menuIconSxProps} />
              {t('PasswordChange')}
            </StyledLink>
          </StyledMenuItem>
          {privacyPolicy && (
            <StyledMenuItem onClick={handleClose}>
              <StyledLink to="/privacy-policy">
                <InvoiceIcon sx={menuIconSxProps} />
                {t('PrivacyPolicy')}
              </StyledLink>
            </StyledMenuItem>
          )}

          <StyledMenuItem>
            <StyledLink to="/logout">
              <ExitIcon sx={menuIconSxProps} />
              Kirjaudu ulos
            </StyledLink>
          </StyledMenuItem>
        </Menu>
      </Container>
    </ThemeProvider>
  );
};

export default memo(UserSettingsPicker);
